import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DashboardSidebar from "../components/DashboardSidebar";
import DashboardHeader from "../components/DashboardHeader";
import MayaLogo from "../components/MayaLogo";
import useVoiceRecorder from "../hooks/useVoiceRecorder";
import {
  Mic,
  MicOff,
  ArrowRight,
  Check,
  ChevronRight,
  Target,
  Sparkles,
  Trophy,
  Type,
  RotateCcw,
  X,
  Home,
  Clock,
  Calculator,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WORD_LIMIT = 200;
const TIME_LIMIT_SEC = 180; // 3 minutes

const CATEGORIES = [
  { key: "communication", label: "Communication" },
  { key: "structure", label: "Structure" },
  { key: "knowledge", label: "Knowledge" },
  { key: "professionalism", label: "Professionalism" },
  { key: "empathy", label: "Empathy" },
];

const countWords = (t) => (t || "").trim().split(/\s+/).filter(Boolean).length;

const clampToWordLimit = (t, limit = WORD_LIMIT) => {
  const words = (t || "").trim().split(/\s+/).filter(Boolean);
  if (words.length <= limit) return t || "";
  return words.slice(0, limit).join(" ");
};

const formatClock = (s) => {
  const m = Math.floor(Math.max(0, s) / 60);
  const r = Math.max(0, s) - m * 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
};

/* ---------- Chart renderers (SVG, no library) ---------- */
const BarChart = ({ chart, dense = false }) => {
  const { labels = [], values = [], unit = "", x_label, y_label } = chart;
  const W = 560, H = 260, pad = 44;
  const max = Math.max(1, ...values);
  const bw = (W - pad * 2) / Math.max(values.length, 1);
  const gap = dense ? 0 : 12; // Histogram = touching bars, Bar = 12px gap
  const inset = dense ? 0 : 6;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <line x1={pad} y1={H - pad} x2={W - pad / 2} y2={H - pad} stroke="#cbd5e1" />
      <line x1={pad} y1={pad / 2} x2={pad} y2={H - pad} stroke="#cbd5e1" />
      {values.map((v, i) => {
        const h = ((v / max) * (H - pad * 2));
        const x = pad + i * bw + inset;
        const y = H - pad - h;
        return (
          <g key={i}>
            <rect x={x} y={y} width={bw - gap} height={h} rx={dense ? 0 : 4} fill="#1d4ed8" opacity="0.85" stroke={dense ? "#ffffff" : "none"} strokeWidth={dense ? 1 : 0} />
            <text x={x + (bw - gap) / 2} y={y - 6} textAnchor="middle" fontSize="10" fill="#334155">
              {v}{unit}
            </text>
            <text x={x + (bw - gap) / 2} y={H - pad + 16} textAnchor="middle" fontSize="10" fill="#64748b">
              {labels[i]}
            </text>
          </g>
        );
      })}
      {y_label && <text x={10} y={pad + 4} fontSize="11" fill="#475569">{y_label}</text>}
      {x_label && <text x={W - pad} y={H - 8} textAnchor="end" fontSize="11" fill="#475569">{x_label}</text>}
    </svg>
  );
};

const LineChart = ({ chart }) => {
  const { labels = [], values = [], unit = "", x_label, y_label } = chart;
  const W = 560, H = 260, pad = 44;
  const max = Math.max(1, ...values);
  const step = (W - pad * 2) / Math.max(values.length - 1, 1);
  const points = values.map((v, i) => `${pad + i * step},${H - pad - (v / max) * (H - pad * 2)}`).join(" ");
  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
      <line x1={pad} y1={H - pad} x2={W - pad / 2} y2={H - pad} stroke="#cbd5e1" />
      <line x1={pad} y1={pad / 2} x2={pad} y2={H - pad} stroke="#cbd5e1" />
      <polyline points={points} fill="none" stroke="#1d4ed8" strokeWidth="2.5" />
      {values.map((v, i) => {
        const x = pad + i * step;
        const y = H - pad - (v / max) * (H - pad * 2);
        return (
          <g key={i}>
            <circle cx={x} cy={y} r="4" fill="#1d4ed8" />
            <text x={x} y={y - 8} textAnchor="middle" fontSize="10" fill="#334155">{v}{unit}</text>
            <text x={x} y={H - pad + 16} textAnchor="middle" fontSize="10" fill="#64748b">{labels[i]}</text>
          </g>
        );
      })}
      {y_label && <text x={10} y={pad + 4} fontSize="11" fill="#475569">{y_label}</text>}
      {x_label && <text x={W - pad} y={H - 8} textAnchor="end" fontSize="11" fill="#475569">{x_label}</text>}
    </svg>
  );
};

const PieChart = ({ chart }) => {
  const { labels = [], values = [] } = chart;
  const total = values.reduce((a, b) => a + b, 0) || 1;
  const colors = ["#1d4ed8", "#3b6ff5", "#60a5fa", "#93c5fd", "#c7d2fe", "#818cf8", "#7c3aed", "#a78bfa"];
  let angle = -Math.PI / 2;
  const cx = 130, cy = 130, r = 100;
  return (
    <div className="flex flex-col md:flex-row items-center gap-6">
      <svg width="260" height="260" viewBox="0 0 260 260">
        {values.map((v, i) => {
          const slice = (v / total) * Math.PI * 2;
          const x1 = cx + r * Math.cos(angle);
          const y1 = cy + r * Math.sin(angle);
          angle += slice;
          const x2 = cx + r * Math.cos(angle);
          const y2 = cy + r * Math.sin(angle);
          const large = slice > Math.PI ? 1 : 0;
          return (
            <path
              key={i}
              d={`M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${large} 1 ${x2} ${y2} Z`}
              fill={colors[i % colors.length]}
              opacity="0.9"
              stroke="#fff"
              strokeWidth="2"
            />
          );
        })}
      </svg>
      <ul className="space-y-1.5">
        {labels.map((l, i) => (
          <li key={i} className="flex items-center gap-2 text-[13px] text-slate-700">
            <span className="w-3 h-3 rounded-sm" style={{ background: colors[i % colors.length] }} />
            <span className="font-medium">{l}</span>
            <span className="text-slate-500">— {values[i]} ({Math.round((values[i] / total) * 100)}%)</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const TableChart = ({ chart }) => {
  const { columns = [], rows = [] } = chart;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="min-w-full text-[13.5px]">
        <thead className="bg-[#f5f8fd]">
          <tr>
            {columns.map((c, i) => (
              <th key={i} className="px-3 py-2 text-left font-semibold text-slate-800 border-b border-slate-200">{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-b-0">
              {r.map((cell, j) => (
                <td key={j} className="px-3 py-2 text-slate-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ChartBlock = ({ chart }) => {
  if (!chart || !chart.type) return null;
  return (
    <section className="mt-4 rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-7">
      {chart.title && <p className="font-semibold text-slate-900 mb-3">{chart.title}</p>}
      {chart.type === "bar" && <BarChart chart={chart} />}
      {chart.type === "histogram" && <BarChart chart={chart} dense />}
      {chart.type === "line" && <LineChart chart={chart} />}
      {chart.type === "pie" && <PieChart chart={chart} />}
      {chart.type === "table" && <TableChart chart={chart} />}
      {!["bar", "histogram", "line", "pie", "table"].includes(chart.type) && (
        <TableChart chart={{ columns: ["Value"], rows: (chart.values || []).map((v) => [String(v)]) }} />
      )}
    </section>
  );
};

/* ---------- Feedback view ---------- */
const ScoreRing = ({ value, size = 132 }) => {
  const stroke = 12;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const pct = Math.max(0, Math.min(10, value)) / 10;
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#eef2f7" strokeWidth={stroke} fill="none" />
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#1d4ed8" strokeWidth={stroke}
          strokeLinecap="round" strokeDasharray={`${c * pct} ${c}`} fill="none" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[32px] font-semibold text-slate-900 leading-none">{value}</span>
        <span className="text-[11px] text-slate-500 mt-1">out of 10</span>
      </div>
    </div>
  );
};

const CategoryBar = ({ label, value }) => (
  <div>
    <div className="flex items-center justify-between text-[13px]">
      <span className="text-slate-700 font-medium">{label}</span>
      <span className="text-slate-500">{value}/10</span>
    </div>
    <div className="mt-1.5 h-2 rounded-full bg-slate-100 overflow-hidden">
      <div className="h-full bg-[#1d4ed8] rounded-full transition-all duration-700" style={{ width: `${(value / 10) * 100}%` }} />
    </div>
  </div>
);

const Feedback = ({ station }) => {
  const f = station.feedback;
  return (
    <div className="grid gap-5">
      <div className="grid lg:grid-cols-[220px_1fr] gap-6 rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
        <div className="flex flex-col items-center justify-center">
          <ScoreRing value={f.overall} />
          <p className="mt-3 text-[12px] uppercase tracking-wider font-semibold text-[#1d4ed8]">Station Score</p>
        </div>
        <div className="space-y-3">
          {CATEGORIES.map((c) => (
            <CategoryBar key={c.key} label={c.label} value={f.scores?.[c.key] ?? 0} />
          ))}
        </div>
      </div>

      {station.station_type === "calculation" && station.correct_answer && (
        <div className="rounded-3xl bg-[#eef4ff] border border-[#dbe6ff] p-6 md:p-7 flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#1d4ed8] text-white flex items-center justify-center shrink-0"><Calculator className="w-4 h-4" /></div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wider text-[#1d4ed8]">Correct answer</p>
            <p className="mt-1 text-[20px] font-semibold text-slate-900">{station.correct_answer}</p>
            {station.answer_explanation && (
              <p className="mt-1.5 text-[13.5px] text-slate-600 leading-relaxed">{station.answer_explanation}</p>
            )}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-5">
        <div className="rounded-3xl bg-[#f0fdf4] border border-[#bbf7d0] p-6 md:p-7">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#15803d] text-white flex items-center justify-center"><Check className="w-4 h-4" /></div>
            <h3 className="font-semibold text-slate-900">Strengths</h3>
          </div>
          <ul className="space-y-2">
            {(f.strengths || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[14.5px] text-slate-700 leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#15803d] shrink-0" />{s}
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-3xl bg-[#fff7ed] border border-[#fed7aa] p-6 md:p-7">
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-lg bg-[#c2410c] text-white flex items-center justify-center"><Target className="w-4 h-4" /></div>
            <h3 className="font-semibold text-slate-900">Areas for Improvement</h3>
          </div>
          <ul className="space-y-2">
            {(f.improvements || []).map((s, i) => (
              <li key={i} className="flex items-start gap-2.5 text-[14.5px] text-slate-700 leading-relaxed">
                <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#c2410c] shrink-0" />{s}
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="rounded-3xl bg-gradient-to-br from-[#eef4ff] to-white border border-[#dbe6ff] p-6 md:p-8">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-8 h-8 rounded-lg bg-[#1d4ed8] text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
          <h3 className="font-semibold text-slate-900">Exemplary Answer</h3>
        </div>
        <p className="text-[15px] text-slate-700 leading-[1.75] whitespace-pre-line">{f.exemplary_answer}</p>
      </div>
    </div>
  );
};

/* ---------- Main page ---------- */
const MayaInterview = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(0);
  const [mode] = useState("text");
  const [manualText, setManualText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);
  const [error, setError] = useState("");
  const [secondsLeft, setSecondsLeft] = useState(TIME_LIMIT_SEC);
  const [timerActive, setTimerActive] = useState(false);
  const [timeExpired, setTimeExpired] = useState(false);
  const timerRef = useRef(null);
  // Stage 4: voice input removed. Typed responses only. Keep a no-op stub so
  // downstream code paths that referenced `voice` remain a small footprint.
  const voice = {
    supported: false,
    listening: false,
    transcript: "",
    start: () => {},
    stop: () => {},
    reset: () => {},
    setManualTranscript: () => {},
  };

  useEffect(() => {
    let mounted = true;
    let cancelled = false;
    const fetchDoc = async () => {
      try {
        const res = await axios.get(`${API}/maya/interviews/${interviewId}`);
        return mounted ? res.data : null;
      } catch (e) {
        if (mounted) setError("Interview not found.");
        return null;
      }
    };
    (async () => {
      let data = await fetchDoc();
      if (!data) { setLoading(false); return; }
      setDoc(data);
      // Poll faster at first — station text is usually ready within a couple of seconds.
      // Backoff after that. Hard-cap at 45s so the UI never hangs indefinitely.
      const delays = [500, 500, 1000, 1500, 2000, 2500, 2500, 3000, 3000, 3000, 4000, 4000, 5000];
      let i = 0;
      const started = Date.now();
      while (data && data.preparing && !cancelled) {
        if (Date.now() - started > 45000) {
          // Timed out — proceed with whatever we have; the station will read
          // "Loading question…" and the user can retry via the retry button.
          break;
        }
        await new Promise((r) => setTimeout(r, delays[Math.min(i, delays.length - 1)]));
        i += 1;
        const next = await fetchDoc();
        if (!next) break;
        data = next;
        setDoc(next);
      }
      if (data?.stations) {
        const first = data.stations.findIndex((s) => !s.completed);
        setCurrent(first === -1 ? data.stations.length - 1 : first);
      }
      setLoading(false);
    })();
    return () => { mounted = false; cancelled = true; };
  }, [interviewId]);

  // Reset input + timer + one-shot submit guard per station
  useEffect(() => {
    setManualText("");
    setSecondsLeft(TIME_LIMIT_SEC);
    setTimerActive(false);
    setTimeExpired(false);
    submittedRef.current = false;
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current]);

  // Timer tick
  useEffect(() => {
    if (!timerActive) return;
    timerRef.current = setInterval(() => {
      setSecondsLeft((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          timerRef.current = null;
          setTimerActive(false);
          setTimeExpired(true);
          voice.stop();
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timerActive]);

  const station = doc?.stations?.[current];
  const answerRaw = mode === "voice" ? voice.transcript : manualText;
  const answerClamped = useMemo(() => clampToWordLimit(answerRaw), [answerRaw]);
  const wordCount = countWords(answerClamped);
  const atLimit = wordCount >= WORD_LIMIT;
  // Student may submit as soon as they have at least a minimal answer.
  // The timer is stopped inside submit() — do NOT gate on !timerActive.
  const canSubmit = answerClamped.trim().length >= 5 && !submitting;

  // Ensure clamped value is written back when the user tries to exceed limit
  useEffect(() => {
    if (mode === "voice" && voice.transcript !== answerClamped) {
      voice.setManualTranscript(answerClamped);
    }
    if (mode === "text" && manualText !== answerClamped) {
      setManualText(answerClamped);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answerRaw, mode]);

  const startTimerIfNeeded = () => {
    if (!timerActive && secondsLeft === TIME_LIMIT_SEC && !timeExpired && !station?.completed) {
      setTimerActive(true);
    }
  };

  const handleStartRecording = () => {
    startTimerIfNeeded();
    voice.start();
  };

  const handleStopRecording = () => {
    voice.stop();
  };

  const submit = useCallback(async (auto = false) => {
    if (!station) return;
    // One-shot guard — protects against any combination of manual click,
    // rapid double-click, and timer-expiry firing at the same moment.
    if (submittedRef.current) return;
    if (!auto && !canSubmit) return;
    if (auto && !answerClamped.trim()) {
      // Timer expired with no answer at all — mark as expired without an API call
      // so no Claude request is spent. Existing empty-answer validation preserved.
      submittedRef.current = true;
      return;
    }
    submittedRef.current = true;
    setSubmitting(true);
    setError("");
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setTimerActive(false);
    voice.stop();
    try {
      await axios.post(
        `${API}/maya/interviews/${interviewId}/stations/${station.index}/submit`,
        { answer: answerClamped.trim(), time_expired: auto || timeExpired }
      );
      const started = Date.now();
      while (Date.now() - started < 180000) {
        await new Promise((r) => setTimeout(r, 2500));
        const res = await axios.get(`${API}/maya/interviews/${interviewId}`);
        const st = res.data.stations.find((s) => s.index === station.index);
        if (st && st.completed && st.feedback) {
          setDoc(res.data);
          break;
        }
        if (st && st.error) {
          setError(st.error || "Feedback generation failed.");
          break;
        }
      }
    } catch (e) {
      // Allow retry if the request itself failed.
      submittedRef.current = false;
      setError(e?.response?.data?.detail || "Feedback failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }, [answerClamped, canSubmit, interviewId, station, timeExpired, voice]);

  // Auto-submit when time expires
  useEffect(() => {
    if (timeExpired && !station?.completed && !submittedRef.current) {
      submit(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeExpired]);

  const goNext = () => {
    if (current < doc.stations.length - 1) {
      setCurrent(current + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/maya"); // Final station completed → return to Maya home
    }
  };

  const done = doc?.stations.filter((s) => s.completed).length ?? 0;
  const total = doc?.stations.length ?? 0;

  return (
    <div className="min-h-screen bg-[#f5f8fd] flex">
      <DashboardSidebar />
      <div className="flex-1 min-w-0">
        <DashboardHeader />
        <main className="p-6 lg:p-10 max-w-[1180px] mx-auto">
          {loading && (
            <div className="flex flex-col items-center justify-center py-24">
              <div className="relative">
                <div className="w-16 h-16 rounded-full bg-[#1d4ed8]/10 flex items-center justify-center">
                  <MayaLogo variant="on-white" className="h-6" />
                </div>
                <span className="absolute inset-0 rounded-full border-2 border-transparent border-t-[#1d4ed8] animate-spin" />
              </div>
              <p className="mt-4 font-semibold text-slate-900">
                {doc?.preparing ? "Maya is preparing your stations…" : "Loading your interview…"}
              </p>
            </div>
          )}
          {!loading && error && !doc && (
            <div className="text-center py-24">
              <p className="text-red-500 mb-4">{error}</p>
              <button onClick={() => navigate("/maya")} className="px-4 py-2 rounded-full bg-[#1d4ed8] text-white text-sm font-medium">Back to Maya</button>
            </div>
          )}
          {!loading && doc && station && (
            <>
              {/* Top bar */}
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-3">
                  <MayaLogo variant="on-white" className="h-6" />
                  <span className="hidden md:inline text-slate-300">|</span>
                  <span className="text-[13.5px] text-slate-600 font-medium">{doc.university}</span>
                </div>
                <div className="flex items-center gap-2 text-[12.5px]">
                  <span className="px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-700 capitalize">{doc.difficulty}</span>
                  <button onClick={() => navigate("/maya")} className="px-3 py-1 rounded-full bg-white border border-slate-200 text-slate-500 hover:text-[#1d4ed8] hover:border-[#1d4ed8] inline-flex items-center gap-1">
                    <X className="w-3 h-3" /> Exit
                  </button>
                </div>
              </div>

              <div className="mt-4 rounded-full h-2 bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#1d4ed8] to-[#3b6ff5] transition-all duration-500" style={{ width: `${(done / total) * 100}%` }} />
              </div>
              <div className="mt-2 flex items-center justify-between text-[12px] text-slate-500">
                <span>Station {station.index} of {total}</span>
                <span>{done} completed</span>
              </div>

              {/* Question */}
              <section className="mt-6 rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-10">
                <div className="flex items-center gap-2">
                  <span className="text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-[#1d4ed8] text-white">
                    Station {station.index}
                  </span>
                  <span className="text-[12px] text-slate-500">·</span>
                  <span className="text-[12.5px] font-medium text-slate-700">{station.theme}</span>
                  {station.station_type === "calculation" && (
                    <span className="ml-2 text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#eef4ff] text-[#1d4ed8]">Calculation</span>
                  )}
                  {station.station_type === "data_interpretation" && (
                    <span className="ml-2 text-[10.5px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-[#eef4ff] text-[#1d4ed8]">Data Interpretation</span>
                  )}
                </div>
                <h1 className="mt-3 text-[26px] md:text-[32px] font-semibold text-slate-900 leading-tight tracking-tight">
                  {station.question}
                </h1>
              </section>

              {/* Chart, if any */}
              <ChartBlock chart={station.chart} />

              {/* Answer area */}
              {!station.completed && (
                <section className="mt-6 rounded-3xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h2 className="text-[18px] font-semibold text-slate-900">Your answer</h2>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-[12.5px] font-semibold ${
                        timeExpired
                          ? "bg-red-50 text-red-600 border border-red-100"
                          : timerActive
                          ? (secondsLeft <= 30 ? "bg-red-50 text-red-600 border border-red-100" : "bg-[#eef4ff] text-[#1d4ed8] border border-[#dbe6ff]")
                          : "bg-slate-50 text-slate-500 border border-slate-200"
                      }`}>
                        <Clock className="w-3.5 h-3.5" />
                        {timeExpired ? "Time up" : formatClock(secondsLeft)}
                      </span>
                      <div className="inline-flex rounded-full bg-slate-100 p-1 text-[12.5px]">
                        <span className="px-3.5 py-1.5 rounded-full inline-flex items-center gap-1.5 font-medium bg-white text-[#1d4ed8] shadow-sm">
                          <Type className="w-3.5 h-3.5" /> Type
                        </span>
                      </div>
                    </div>
                  </div>

                  {mode === "voice" && voice.supported && (
                    <div className="mt-5">
                      <div className={`rounded-2xl border p-5 transition ${voice.listening ? "border-[#1d4ed8] bg-[#eef4ff]" : "border-slate-200 bg-slate-50"}`}>
                        <div className="flex items-center gap-4">
                          <button
                            onClick={voice.listening ? handleStopRecording : handleStartRecording}
                            disabled={timeExpired}
                            className={`w-16 h-16 rounded-full flex items-center justify-center transition shadow-lg ${
                              voice.listening ? "bg-red-500 text-white animate-pulse" : "bg-[#1d4ed8] text-white hover:bg-[#1e40af]"
                            } disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {voice.listening ? <MicOff className="w-7 h-7" /> : <Mic className="w-7 h-7" />}
                          </button>
                          <div className="flex-1">
                            <p className="font-semibold text-slate-900">
                              {timeExpired ? "Time is up" : voice.listening ? "Listening…" : "Tap to speak your answer"}
                            </p>
                            <p className="text-[12.5px] text-slate-500">
                              Live transcription. Edit before you submit. The 3-minute timer starts when you begin.
                            </p>
                          </div>
                          {voice.listening && (
                            <div className="flex items-end gap-0.5 h-8">
                              {[10, 18, 14, 22, 12, 20, 16].map((h, i) => (
                                <span key={i} className="w-1 rounded-full bg-[#1d4ed8] animate-pulse" style={{ height: h, animationDelay: `${i * 90}ms` }} />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <textarea
                        value={voice.transcript}
                        onChange={(e) => {
                          startTimerIfNeeded();
                          voice.setManualTranscript(clampToWordLimit(e.target.value));
                        }}
                        rows={7}
                        placeholder="Your transcribed answer will appear here…"
                        disabled={timeExpired}
                        style={{ resize: "none", height: 200 }}
                        className="mt-4 w-full rounded-2xl border border-slate-200 p-4 text-[15px] leading-relaxed focus:outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-[#1d4ed8]/10 disabled:bg-slate-50"
                      />
                      {voice.error && <p className="mt-2 text-[13px] text-red-500">{voice.error}</p>}
                    </div>
                  )}

                  {mode === "voice" && !voice.supported && (
                    <p className="mt-4 text-[13.5px] text-slate-500">
                      Voice input isn’t supported in this browser. Please switch to <span className="font-medium">Type</span>.
                    </p>
                  )}

                  {mode === "text" && (
                    <textarea
                      value={manualText}
                      onChange={(e) => {
                        startTimerIfNeeded();
                        setManualText(clampToWordLimit(e.target.value));
                      }}
                      rows={8}
                      placeholder="Type your MMI answer here…"
                      disabled={timeExpired}
                      style={{ resize: "none", height: 220, maxHeight: 220, overflow: "hidden" }}
                      className="mt-5 w-full rounded-2xl border border-slate-200 p-4 text-[15px] leading-relaxed focus:outline-none focus:border-[#1d4ed8] focus:ring-2 focus:ring-[#1d4ed8]/10 disabled:bg-slate-50"
                    />
                  )}

                  <div className="mt-3 flex items-center justify-between text-[12.5px]">
                    <span className={`${atLimit ? "text-red-600 font-semibold" : "text-slate-500"}`}>
                      {wordCount}/{WORD_LIMIT} words
                    </span>
                    <span className="text-slate-400">Max 200 words · 3-minute limit</span>
                  </div>

                  {error && <p className="mt-3 text-[13px] text-red-500">{error}</p>}

                  <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
                    <button
                      onClick={() => { voice.reset(); setManualText(""); }}
                      disabled={timeExpired}
                      className="text-[13px] text-slate-500 hover:text-slate-800 inline-flex items-center gap-1 disabled:opacity-50"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Clear
                    </button>
                    <button
                      onClick={() => submit(false)}
                      disabled={submitting || answerClamped.trim().length < 5}
                      className="px-6 py-3 rounded-full bg-[#1d4ed8] text-white font-semibold hover:bg-[#1e40af] transition inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                    >
                      {submitting ? "Maya is marking your answer…" : (<>Submit answer <ArrowRight className="w-4 h-4" /></>)}
                    </button>
                  </div>
                </section>
              )}

              {station.completed && station.feedback && (
                <div className="mt-8">
                  <div className="flex items-center gap-2 mb-4">
                    <MayaLogo variant="on-white" className="h-5" />
                    <span className="text-[12px] font-semibold uppercase tracking-wider text-[#1d4ed8]">Feedback for Station {station.index}</span>
                  </div>
                  <Feedback station={station} />

                  <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
                    <p className="text-[13px] text-slate-500">
                      {current < doc.stations.length - 1
                        ? "Ready for the next station?"
                        : "You’ve completed the final station. This interview is saved to your Previous Interviews."}
                    </p>
                    {current < doc.stations.length - 1 ? (
                      <button onClick={goNext} className="px-6 py-3 rounded-full bg-[#1d4ed8] text-white font-semibold hover:bg-[#1e40af] transition inline-flex items-center gap-2 shadow-sm">
                        Next Station <ChevronRight className="w-4 h-4" />
                      </button>
                    ) : (
                      <button onClick={() => navigate("/maya")} className="px-6 py-3 rounded-full bg-[#15803d] text-white font-semibold hover:bg-[#166534] transition inline-flex items-center gap-2 shadow-sm">
                        <Home className="w-4 h-4" /> Return to Maya
                      </button>
                    )}
                  </div>
                </div>
              )}

              <div className="mt-10 flex flex-wrap items-center gap-2 justify-center">
                {doc.stations.map((s, i) => (
                  <button
                    key={s.index}
                    onClick={() => setCurrent(i)}
                    className={`w-9 h-9 rounded-full text-[12.5px] font-semibold border transition ${
                      i === current
                        ? "bg-[#1d4ed8] text-white border-[#1d4ed8]"
                        : s.completed
                        ? "bg-[#e6f7ee] text-[#15803d] border-[#c1e9d0]"
                        : "bg-white text-slate-500 border-slate-200 hover:border-[#1d4ed8]"
                    }`}
                  >
                    {s.completed ? <Check className="w-4 h-4 mx-auto" /> : s.index}
                  </button>
                ))}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default MayaInterview;
